import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  FiHome,
  FiUserPlus,
  FiUsers,
  FiMessageSquare,
  FiBarChart2,
  FiSend,
  FiUser,
  FiFlag,
  FiShield,
  FiMap,
  FiSettings
} from "react-icons/fi";

const AdminSidebar = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, hasPermission, isSuperAdmin, hasAnyPermission } = useAuth();

  // Get the correct dashboard path based on user role
  const getDashboardPath = () => {
    if (user?.role === 'state-admin') {
      return "/admin/state-dashboard";
    } else if (user?.role === 'sd-admin') {
      return "/admin/senatorial-dashboard";
    } else if (user?.role === 'fc-admin') {
      return "/admin/federal-constituency-dashboard";
    } else if (user?.role === 'lga-admin') {
      return "/admin/lga-dashboard";
    } else if (user?.role === 'lcda-admin') {
      return "/admin/lcda-dashboard";
    } else if (user?.role === 'ward-admin') {
      return "/admin/ward-dashboard";
    } else if (user?.role === 'polling-unit-admin') {
      return "/admin/polling-unit-dashboard";
    } else {
      return "/admin/dashboard"; // Default for super-admin
    }
  };

  // Check if current path is active (handles different dashboard paths)
  const isActivePath = (path) => {
    if (path === "/admin/dashboard") {
      // For dashboard, check if we're on any dashboard page
      return location.pathname.includes("dashboard");
    }
    return location.pathname === path;
  };

  // Check if admin can create admins below them
  const canCreateAdmins = () => {
    if (isSuperAdmin) return true;
    if (user?.role === 'state-admin') {
      return false; // State admin cannot see Permissions menu
    }
    if (user?.role === 'sd-admin') {
      return hasAnyPermission(["create-fc-admin", "create-lga-admin", "create-lcda-admin"]);
    }
    if (user?.role === 'fc-admin') {
      return hasAnyPermission(["create-lcda-admin", "create-ward-admin"]);
    }
    if (user?.role === 'lga-admin') {
      return hasAnyPermission(["create-lcda-admin", "create-ward-admin"]);
    }
    if (user?.role === 'lcda-admin') {
      return hasPermission("create-ward-admin");
    }
    if (user?.role === 'ward-admin') {
      return hasPermission("create-polling-unit-admin");
    }
    return false;
  };

  // Check if admin can view other admins
  const canViewAdmins = () => {
    if (isSuperAdmin) return true;
    if (user?.role === 'state-admin') {
      return true; // State admin CAN see View Admins menu
    }
    if (user?.role === 'sd-admin') {
      return hasAnyPermission(["create-fc-admin", "create-lga-admin", "create-lcda-admin"]);
    }
    if (["fc-admin", "lga-admin", "lcda-admin", "ward-admin"].includes(user?.role)) {
      return canCreateAdmins();
    }
    return false;
  };

  // Check if admin can manage locations
  const canManageLocations = () => {
    return isSuperAdmin && hasAnyPermission([
      "manage-states", "manage-senatorial-districts", "manage-federal-constituencies",
      "manage-local-governments", "manage-lcdas", "manage-wards", "manage-polling-units"
    ]);
  };

  // Check if admin can manage system
  const canManageSystem = () => {
    return isSuperAdmin && hasAnyPermission([
      "manage-system", "view-system-logs", "manage-users", "manage-roles", "manage-permissions"
    ]);
  };

  // Check if admin can manage members
  const canManageMembers = () => {
    return hasAnyPermission([
      "view-members", "create-members", "edit-members", "delete-members",
      "approve-members", "suspend-members", "reject-members", "reactivate-members"
    ]);
  };

  // Check if admin can broadcast - FC Admin specific
  const canBroadcast = () => {
    if (user?.role === 'fc-admin') {
      return hasAnyPermission([
        "broadcast-announcement-federal-constituency",
        "broadcast-announcement-local-government", 
        "broadcast-announcement-lcda",
        "broadcast-announcement-ward",
        "broadcast-announcement-polling-unit",
        "broadcast-request-federal-constituency",
        "broadcast-request-local-government",
        "broadcast-request-lcda",
        "broadcast-request-ward",
        "broadcast-request-polling-unit"
      ]);
    }
    return hasAnyPermission([
      "broadcast-announcement-nationwide",
      "broadcast-announcement-state",
      "broadcast-announcement-senatorial-district",
      "broadcast-announcement-federal-constituency",
      "broadcast-announcement-local-government",
      "broadcast-announcement-lcda",
      "broadcast-announcement-ward",
      "broadcast-announcement-polling-unit",
      "broadcast-request-nationwide",
      "broadcast-request-state",
      "broadcast-request-senatorial-district",
      "broadcast-request-federal-constituency",
      "broadcast-request-local-government",
      "broadcast-request-lcda",
      "broadcast-request-ward",
      "broadcast-request-polling-unit"
    ]);
  };

  // Check if admin can create group chats - FC Admin specific
  const canCreateGroupChats = () => {
    if (user?.role === 'fc-admin') {
      return hasPermission("create-group-chat-federal-constituency");
    }
    return hasAnyPermission([
      "create-group-chat-nationwide",
      "create-group-chat-state",
      "create-group-chat-senatorial-district",
      "create-group-chat-federal-constituency",
      "create-group-chat-local-government",
      "create-group-chat-lcda",
      "create-group-chat-ward",
      "create-group-chat-polling-unit"
    ]);
  };

  // Check if admin can view reports - FC Admin specific
  const canViewReports = () => {
    if (user?.role === 'fc-admin') {
      return hasAnyPermission([
        "view-reports",
        "view-fc-reports",
        "view-lga-reports", 
        "view-lcda-reports",
        "view-ward-reports",
        "view-polling-unit-reports"
      ]);
    }
    return hasAnyPermission([
      "view-reports",
      "view-nationwide-reports",
      "view-state-reports",
      "view-sd-reports",
      "view-fc-reports",
      "view-lga-reports",
      "view-lcda-reports",
      "view-ward-reports",
      "view-polling-unit-reports"
    ]);
  };

  // Simple menu items - organized by functionality
  const getMenuItems = () => {
    const dashboardPath = getDashboardPath();
    
    const items = [
      // Dashboard - Always available for all admins
      { 
        name: "Dashboard", 
        path: dashboardPath, 
        icon: <FiHome />,
        allowed: true
      },
      
      // Profile - Always available for all admins
      {
        name: "Profile",
        path: "/admin/profile",
        icon: <FiUser />,
        allowed: true
      },

      // Members Management - FC Admin can manage members
      {
        name: "View Members",
        path: "/admin/view-members",
        icon: <FiUsers />,
        allowed: canManageMembers()
      },

      // Admin Management - State admin CANNOT see Permissions but CAN see View Admins
      {
        name: "Permissions",
        path: "/admin/create-admin",
        icon: <FiUserPlus />,
        allowed: canCreateAdmins()
      },
      {
        name: "View Admins",
        path: "/admin/view-admins",
        icon: <FiUsers />,
        allowed: canViewAdmins()
      },

      // Communication - FC Admin can create group chats and broadcast
      {
        name: "Group Chat",
        path: "/admin/create-group-chat",
        icon: <FiMessageSquare />,
        allowed: canCreateGroupChats()
      },
      {
        name: "Broadcast",
        path: "/admin/broadcast",
        icon: <FiSend />,
        allowed: canBroadcast()
      },

      // Reports & Analytics - FC Admin can view reports
      {
        name: "Reports",
        path: "/admin/reports",
        icon: <FiBarChart2 />,
        allowed: canViewReports()
      },

      // System Management (Super Admin only) - State admin CANNOT access these
      {
        name: "Roles Management",
        path: "/admin/roles-management",
        icon: <FiShield />,
        allowed: isSuperAdmin // ONLY super-admin can see this
      },
      {
        name: "Create Custom Role",
        path: "/admin/create-custom-role",
        icon: <FiShield />,
        allowed: isSuperAdmin // ONLY super-admin can see this
      },
      {
        name: "Location Management",
        path: "/admin/location-management",
        icon: <FiMap />,
        allowed: canManageLocations()
      },
      {
        name: "System Settings",
        path: "/admin/system-settings",
        icon: <FiSettings />,
        allowed: canManageSystem()
      },
      {
        name: "Brand Identity",
        path: "/admin/manage-brand",
        icon: <FiFlag />,
        allowed: isSuperAdmin && hasPermission("manage-branding")
      },
    ];

    return items.filter(item => item.allowed);
  };

  const handleItemClick = (path) => {
    navigate(path);
    if (window.innerWidth < 768) {
      onClose();
    }
  };

  const menuItems = getMenuItems();

  return (
    <>
      <style>
        {`
          .admin-sidebar {
            width: 250px;
            background-color: #ffffff;
            border-right: 1px solid #e6ecf5;
            padding: 25px 20px;
            display: flex;
            flex-direction: column;
            height: 100vh;
            position: fixed;
            left: 0;
            top: 0;
            overflow-y: auto;
            box-shadow: 2px 0 8px rgba(0, 0, 0, 0.05);
            z-index: 1000;
            transform: translateX(-100%);
            transition: transform 0.3s ease-in-out;
          }

          .admin-sidebar.open {
            transform: translateX(0);
          }

          .sidebar-header {
            text-align: left;
            margin-bottom: 20px;
          }

          .sidebar-header h1 {
            font-weight: 700;
            color: #007bff;
            font-size: 20px;
            margin-bottom: 5px;
            letter-spacing: 0.3px;
          }

          .sidebar-header p {
            color: #666;
            font-size: 13px;
            margin-bottom: 0;
          }

          .sidebar-item {
            display: flex;
            align-items: center;
            gap: 12px;
            font-size: 15px;
            color: #555;
            cursor: pointer;
            padding: 10px 14px;
            font-weight: normal;
            background-color: transparent;
            border-radius: 8px;
            margin-bottom: 4px;
            position: relative;
            transition: all 0.2s ease;
          }

          .sidebar-item:hover {
            background-color: #f8f9fa;
          }

          .sidebar-item.active {
            color: #007bff;
            font-weight: 600;
            background-color: #f0f6ff;
          }

          .sidebar-item.active::before {
            content: "";
            position: absolute;
            left: 0;
            top: 0;
            height: 100%;
            width: 4px;
            background-color: #007bff;
            border-top-right-radius: 4px;
            border-bottom-right-radius: 4px;
          }

          .sidebar-item-icon {
            font-size: 17px;
            flex-shrink: 0;
          }

          .sidebar-overlay {
            display: block;
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: rgba(0, 0, 0, 0.4);
            z-index: 999;
          }

          @media (min-width: 768px) {
            .admin-sidebar {
              transform: translateX(0);
            }
            .sidebar-overlay {
              display: none;
            }
          }

          @media (max-width: 767px) {
            .admin-sidebar {
              width: 280px;
            }
          }

          .admin-sidebar::-webkit-scrollbar {
            width: 4px;
          }
          .admin-sidebar::-webkit-scrollbar-track {
            background: #f1f1f1;
          }
          .admin-sidebar::-webkit-scrollbar-thumb {
            background: #c1c1c1;
            border-radius: 4px;
          }
          .admin-sidebar::-webkit-scrollbar-thumb:hover {
            background: #a8a8a8;
          }
        `}
      </style>

      {/* Overlay for Mobile */}
      {isOpen && <div className="sidebar-overlay" onClick={onClose}></div>}

      {/* Sidebar */}
      <div className={`admin-sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h1>Admin Portal</h1>
          <p>{user?.role ? `${user.role.replace('-', ' ').toUpperCase()}` : 'Administration'}</p>
        </div>

        {menuItems.length > 0 && (
          <>
            {menuItems.map((item, index) => (
              <div
                key={index}
                className={`sidebar-item ${isActivePath(item.path) ? 'active' : ''}`}
                onClick={() => handleItemClick(item.path)}
              >
                <div className="sidebar-item-icon">{item.icon}</div>
                <span>{item.name}</span>
              </div>
            ))}
          </>
        )}

        {menuItems.length === 0 && (
          <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
            <p>No menu items available for your role.</p>
          </div>
        )}
      </div>
    </>
  );
};

export default AdminSidebar;